import { Component, Inject, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { faSpinner, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { finalize } from "rxjs/operators";
import { ReminderInterval, ReminderType } from "src/app/models/Enums";
import { Reminder } from "src/app/models/Reminder";
import { GlobalOverlayService } from "src/app/services/global-overlay.service";
import { ReminderService } from "src/app/services/reminder.service";

import { DEFAULT_DAYS_AFTER, DEFAULT_DAYS_BEFORE } from "./../../services/reminder.service";

@Component({
  selector: "reminder-modal",
  templateUrl: "./reminder-modal.component.html",
  styleUrls: ["./reminder-modal.component.scss"],
  standalone: false,
})
export class ReminderModalComponent implements OnInit {
  faSpinner = faSpinner;
  faClose = faTimesCircle;
  isLoading: boolean;
  isTypesLoading: boolean;
  reminder: Reminder;
  newReminder: boolean;
  reminders: Reminder[];
  reminderAlreadyExists: boolean;

  constructor(
    private globalOverlayService: GlobalOverlayService,
    public reminderService: ReminderService,
    public dialogRef: MatDialogRef<ReminderModalComponent, Reminder>,
    @Inject(MAT_DIALOG_DATA) public data: Reminder,
  ) {}

  ngOnInit() {
    this.reminder = Object.assign({}, this.data);
    this.newReminder = !this.reminder?.id;

    if (this.newReminder) this.getReminders();
  }

  onReminderTypeChange() {
    this.reminderAlreadyExists = this.reminders.some((x) => x.type === this.reminder.type);

    this.setDefaultFields();
  }

  setDefaultFields() {
    // * Set default fields based on reminder type
    switch (this.reminder.type) {
      case ReminderType.Receive:
      case ReminderType.Shipping:
        this.reminder.interval = ReminderInterval.Daily;
        break;
      case ReminderType.PO:
        this.reminder.interval = ReminderInterval.Weekly;
        this.reminder.daysInAdvance = DEFAULT_DAYS_AFTER;
        this.reminder.to = null;
        break;
      case ReminderType.Service:
      case ReminderType.Calibration:
        this.reminder.interval = ReminderInterval.Weekly;
        this.reminder.daysInAdvance = DEFAULT_DAYS_BEFORE;
        break;
    }
  }

  getReminders() {
    this.isLoading = true;
    this.isTypesLoading = true;
    this.reminderService.get(this.reminder.companyId).subscribe(
      (response) => {
        this.isLoading = false;
        this.isTypesLoading = false;
        this.reminders = response;
      },
      () => {
        this.isLoading = false;
        this.isTypesLoading = false;
      },
    );
  }

  onSave() {
    if (this.newReminder) {
      this.createReminder(this.reminder);
    } else {
      this.updateReminder(this.reminder);
    }
  }

  onCancel() {
    // no need to emit any result
    this.dialogRef.close();
  }

  isPO(r: Reminder) {
    return r.type === ReminderType.PO;
  }

  // Notifications are Receive and Shipping
  isNotification(r: Reminder) {
    return r.type === ReminderType.Receive || r.type === ReminderType.Shipping;
  }

  isServiceCalibration(r: Reminder) {
    return r.type === ReminderType.Service || r.type === ReminderType.Calibration;
  }

  get daysList() {
    if (this.reminder.type === ReminderType.PO) return [7, 10, 15, 20, 25, 30, 45, 60, 75, 90];

    return [1, 5, 7, 10, 15, 20, 25, 30, 45, 60, 75, 90];
  }

  trackByFn(item: string) {
    return item;
  }

  private createReminder(reminder: Reminder) {
    this.globalOverlayService.show();
    this.reminderService
      .create(reminder)
      .pipe(finalize(() => this.globalOverlayService.close()))
      .subscribe((result) => this.dialogRef.close(result));
  }

  private updateReminder(reminder: Reminder) {
    this.globalOverlayService.show();
    this.reminderService
      .update(reminder, reminder.id)
      .pipe(finalize(() => this.globalOverlayService.close()))
      .subscribe(() => this.dialogRef.close(reminder));
  }
}
